import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard, FeatureGuard, ModulePermissionGuard } from 'src/auth/guard';
import { MotorGuard, AppGuard, ScanGuard } from 'src/auth/guard/guard-helpers';
import { LoadingScreen } from 'src/components/loading-screen';
import DashboardLayout from 'src/layouts/dashboard';

// ----------------------------------------------------------------------

// DASHBOARD HOME
const DashboardHomePage = lazy(() => import('src/modules/dashboard/components/dashboard-home'));

// NPS SEARCH
const NpsSearchPage = lazy(() => import('src/modules/nps-search/components/nps-search'));

// RULES
const RulesPage = lazy(() => import('src/modules/rules/components/rules'));
const CreateRulePage = lazy(() => import('src/modules/rules/components/create-rule'));
const EditRulePage = lazy(() => import('src/modules/rules/components/create-rule'));

// CONVERSION RATES
const ConversionRatePage = lazy(() => import('src/modules/conversion-rates/components/conversion-rates'));
const CreateConversionRatePage = lazy(() => import('src/modules/conversion-rates/components/conversion-rate-form'));
const EditConversionRatePage = lazy(() => import('src/modules/conversion-rates/components/conversion-rate-form'));

// VARIABLES
const VariablesPage = lazy(() => import('src/modules/variables/components/variables'));

// INVOICES
const InvoicesPage = lazy(() => import('src/modules/invoices/components/invoices'));
const ReviewInvoicePage = lazy(() => import('src/modules/invoices/components/invoice-form'));

// USERS
const AdminUserPage = lazy(() => import('src/modules/admin-users/components/admin-users'));
const CreateAdminUserPage = lazy(() => import('src/modules/admin-users/components/create-admin-users'));
const EditAdminUserPage = lazy(() => import('src/modules/admin-users/components/create-admin-users'));

// ADMINLOGS
const AdminLogsPage = lazy(() => import('src/modules/adminlogs/components/adminlogs'));

// APP USERS
const AppUsersPage = lazy(() => import('src/modules/app-users/components/app-users'));
const AppUserDetailsPage = lazy(() => import('src/modules/app-users/components/app-user-details'));
const AppUserEditPage = lazy(() => import('src/modules/app-users/components/app-user-edit'));

// WEB APP USERS
const WebAppUsersPage = lazy(() => import('src/modules/web-app-users/components/web-app-users'));
const CreateWebAppUserPage = lazy(() => import('src/modules/web-app-users/components/create-web-app-users'));
const EditWebAppUserPage = lazy(() => import('src/modules/web-app-users/components/create-web-app-users'));

// TERMS OF USE
const TermsOfUsePage = lazy(() => import('src/modules/terms-of-use/components/terms-of-use'));
const CreateTermsOfUsePage = lazy(() => import('src/modules/terms-of-use/components/create-terms-of-use'));


const InstitutoForteTermsOfUsePage = lazy(() => import('src/modules/instituto-terms/components/instituto-terms-of-use'));
const CreateInstitutoForteOfUsePage = lazy(() => import('src/modules/instituto-terms/components/create-instituto-terms-of-use'));


// PRIVACY AND POLICY
const PrivacyPolicyPage = lazy(() => import('src/modules/privacy-policy/components/privacy-policy'));
const CreatePrivacyPolicyPage = lazy(() => import('src/modules/privacy-policy/components/create-privacy-policy'));

// FAQ
const FaqPage = lazy(() => import('src/modules/faq/components/faq'));
const CreateFaqPage = lazy(() => import('src/modules/faq/components/faq-form'));
const EditFaqPage = lazy(() => import('src/modules/faq/components/faq-form'));
const FaqCategoriesPage = lazy(() => import('src/modules/faq/components/faq-categories'));
const CreateFaqCategoryPage = lazy(() => import('src/modules/faq/components/faq-category-form'));
const EditFaqCategoryPage = lazy(() => import('src/modules/faq/components/faq-category-form'));

// SHOPPING SETTINGS
const ShoppingSettingsPage = lazy(() => import('src/modules/shopping-settings/components/shopping-settings'));
const ShoppingSettingsEditPage = lazy(() => import('src/modules/shopping-settings/components/shopping-settings-form'));

// USER LEVELS
const UserLevelsPage = lazy(() => import('src/modules/user-levels/components/user-levels'));

// ESTABLISHMENTS
const EstablishmentsPage = lazy(() => import('src/modules/establishments/components/establishments'));
const CreateEstablishmentPage = lazy(() => import('src/modules/establishments/components/establishment-form'));
const EditEstablishmentPage = lazy(() => import('src/modules/establishments/components/establishment-form'));
const EstablishmentCategoriesPage = lazy(() => import('src/modules/establishments/components/establishment-categories'));
const CreateEstablishmentCategoryPage = lazy(() => import('src/modules/establishments/components/establishment-categories-form'));
const EditEstablishmentCategoryPage = lazy(() => import('src/modules/establishments/components/establishment-categories-form'));

// SORTEIOS
const GiveawaysPage = lazy(() => import('src/modules/giveaways/components/giveaways'));
const GiveawayFormPage = lazy(() => import('src/modules/giveaways/components/giveaway-form'));

// CAMPANHAS
const CampaignsPage = lazy(() => import('src/modules/campaigns/components/campaigns'));
const CampaignFormPage = lazy(() => import('src/modules/campaigns/components/campaign-form'));

// BENEFITS (GENÉRICOS)
const BenefitsPage = lazy(() => import('src/modules/benefits/components/benefits'));
const BenefitFormPage = lazy(() => import('src/modules/benefits/components/benefit-form'));

// REDEEMS
const RedeemsView = lazy(() => import('src/modules/redeems/components/redeems'));

// KEEP UP (FIQUE POR DENTRO)
const KeepUpPage = lazy(() => import('src/modules/keep-up/components/keep-up'));
const KeepUpFormPage = lazy(() => import('src/modules/keep-up/components/keep-up-form'));

// HIGHLIGHTS (DESTAQUES)
const HighlightsPage = lazy(() => import('src/modules/highlights/components/highlights'));
const HighlightFormPage = lazy(() => import('src/modules/highlights/components/highlight-form'));

// STORIES
const StoriesPage = lazy(() => import('src/modules/stories/components/stories'));
const StoryFormPage = lazy(() => import('src/modules/stories/components/story-form'));

// BANNERS
const BannersPage = lazy(() => import('src/modules/banners/components/banners'));

// HOME
const HomePage = lazy(() => import('src/modules/home/components/home'));
const HomeFormPage = lazy(() => import('src/modules/home/components/home-form'));

// NOTIFICATIONS
const NotificationCampaignsPage = lazy(() => import('src/modules/notifications/components/notifications'));
const CampaignNotificationFormPage = lazy(() => import('src/modules/notifications/components/notification-form'));
const ProximityNotificationsPage = lazy(() => import('src/modules/notifications/components/proximity-notifications'));
const ProximityNotificationFormPage = lazy(() => import('src/modules/notifications/components/proximity-notification-form'));
const NotificationLogsPage = lazy(() => import('src/modules/notifications/components/notification-logs'));
const SegmentsListPage = lazy(() => import('src/modules/notification-segments/components/segments'));
const SegmentFormPage = lazy(() => import('src/modules/notification-segments/components/segment-form'));

// ROULETTE
const RouletteSpinsPage = lazy(() => import('src/modules/roulette/components/roulette-spins'));
const RoulettePrizesPage = lazy(() => import('src/modules/roulette/components/roulette-prizes'));
const RoulettePrizeFormPage = lazy(() => import('src/modules/roulette/components/roulette-prize-form'));

// MISSIONS
const MissionsPage = lazy(() => import('src/modules/missions/components/missions'));
const MissionFormPage = lazy(() => import('src/modules/missions/components/mission-form'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      // CAMADA 1: AuthGuard - Verifica se está autenticado
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      // =================================================================
      // HOME - Dashboard Home Page
      // =================================================================
      {
        element: <DashboardHomePage />,
        index: true,
      },

      // =================================================================
      // SEÇÃO MOTOR - Feature: 'motor'
      // =================================================================

      // MOTOR RULES
      {
        path: 'rules',
        children: [
          {
            element: (
              <MotorGuard module="motorRules" action="view">
                <RulesPage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <MotorGuard module="motorRules" action="create">
                <CreateRulePage />
              </MotorGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <MotorGuard module="motorRules" action="edit">
                <EditRulePage />
              </MotorGuard>
            ),
          },
        ],
      },

      // CONVERSION RATES
      {
        path: 'conversionrate',
        children: [
          {
            element: (
              <MotorGuard module="motorConversionRates" action="view">
                <ConversionRatePage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <MotorGuard module="motorConversionRates" action="create">
                <CreateConversionRatePage />
              </MotorGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <MotorGuard module="motorConversionRates" action="edit">
                <EditConversionRatePage />
              </MotorGuard>
            ),
          },
        ],
      },

      // MOTOR VARIABLES
      {
        path: 'variables',
        element: (
          <MotorGuard module="motorVariables" action="view">
            <VariablesPage />
          </MotorGuard>
        ),
      },

      // INVOICES (NOTAS FISCAIS)
      {
        path: 'invoices',
        element: (
          <ScanGuard module="invoices" action="view">
            <InvoicesPage />
          </ScanGuard>
        ),
      },
      {
        path: 'invoices/:id',
        element: (
          <ScanGuard module="invoices" action="edit">
            <ReviewInvoicePage />
          </ScanGuard>
        ),
      },

      // ADMIN USERS (Configurações Gerais - sem dependência de feature)
      {
        path: 'admin-user',
        children: [
          {
            element: (
              <ModulePermissionGuard module="adminUsers" action="view">
                <AdminUserPage />
              </ModulePermissionGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <ModulePermissionGuard module="adminUsers" action="create">
                <CreateAdminUserPage />
              </ModulePermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <ModulePermissionGuard module="adminUsers" action="edit">
                <EditAdminUserPage />
              </ModulePermissionGuard>
            ),
          },
        ],
      },

      // ADMIN LOGS (só precisa da feature motor, sem módulo específico)
      {
        path: 'adminlogs',
        element: (
          <FeatureGuard feature="motor">
            <AdminLogsPage />
          </FeatureGuard>
        ),
      },

      // USUÁRIOS DO WEB APP (Configurações Gerais - sem dependência de feature)
      {
        path: 'web-app-users',
        children: [
          {
            element: (
              <ModulePermissionGuard module="webappUsers" action="view">
                <WebAppUsersPage />
              </ModulePermissionGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <ModulePermissionGuard module="webappUsers" action="create">
                <CreateWebAppUserPage />
              </ModulePermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <ModulePermissionGuard module="webappUsers" action="edit">
                <EditWebAppUserPage />
              </ModulePermissionGuard>
            ),
          },
        ],
      },

      // =================================================================
      // SEÇÃO MOTOR - CAMPANHAS E SORTEIOS
      // =================================================================

      // GIVEAWAYS (SORTEIOS)
      {
        path: 'giveaways',
        children: [
          {
            element: (
              <MotorGuard module="motorGiveaways" action="view">
                <GiveawaysPage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <MotorGuard module="motorGiveaways" action="create">
                <GiveawayFormPage />
              </MotorGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <MotorGuard module="motorGiveaways" action="edit">
                <GiveawayFormPage />
              </MotorGuard>
            ),
          },
        ],
      },

      // CAMPAIGNS
      {
        path: 'campaigns',
        children: [
          {
            element: (
              <MotorGuard module="motorCampaigns" action="view">
                <CampaignsPage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <MotorGuard module="motorCampaigns" action="create">
                <CampaignFormPage />
              </MotorGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <MotorGuard module="motorCampaigns" action="edit">
                <CampaignFormPage />
              </MotorGuard>
            ),
          },
        ],
      },

      // =================================================================
      // SEÇÃO APP - Feature: 'app'
      // =================================================================

      // USUÁRIOS DO APP
      {
        path: 'app-users',
        children: [
          {
            index: true,
            element: (
              <AppGuard module="appUsers" action="view">
                <AppUsersPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appUsers" action="view">
                <AppUserDetailsPage />
              </AppGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <AppGuard module="appUsers" action="edit">
                <AppUserEditPage />
              </AppGuard>
            ),
          },
        ],
      },

      // EVENTS
      {
        path: 'events',
        children: [
          {
            element: (
              <AppGuard module="appEvents" action="view">
                <BenefitsPage benefitType="event" />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appEvents" action="create">
                <BenefitFormPage benefitType="event" />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appEvents" action="edit">
                <BenefitFormPage benefitType="event" />
              </AppGuard>
            ),
          },
        ],
      },

      // COUPONS (NOVO)
      {
        path: 'coupons',
        children: [
          {
            element: (
              <AppGuard module="appCoupons" action="view">
                <BenefitsPage benefitType="coupon" />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appCoupons" action="create">
                <BenefitFormPage benefitType="coupon" />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appCoupons" action="edit">
                <BenefitFormPage benefitType="coupon" />
              </AppGuard>
            ),
          },
        ],
      },

      // PRODUCTS (NOVO)
      {
        path: 'products',
        children: [
          {
            element: (
              <AppGuard module="appProducts" action="view">
                <BenefitsPage benefitType="product" />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appProducts" action="create">
                <BenefitFormPage benefitType="product" />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appProducts" action="edit">
                <BenefitFormPage benefitType="product" />
              </AppGuard>
            ),
          },
        ],
      },

      // REDEEMS
      {
        path: 'redeems',
        element: (
          <AppGuard module="appBenefitRedeems" action="view">
            <RedeemsView />
          </AppGuard>
        ),
      },

      // TERMS OF USE
      {
        path: 'terms',
        children: [
          {
            element: (
              <AppGuard module="appTermsOfUse" action="view">
                <TermsOfUsePage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appTermsOfUse" action="create">
                <CreateTermsOfUsePage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appTermsOfUse" action="edit">
                <CreateTermsOfUsePage />
              </AppGuard>
            ),
          },
        ],
      },


        {
        path: 'instituto-forte-terms',
        children: [
          {
            element: (
              <AppGuard module="appTermsInstitutoForte" action="view">
                <InstitutoForteTermsOfUsePage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appTermsInstitutoForte" action="create">
                <CreateInstitutoForteOfUsePage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appTermsInstitutoForte" action="edit">
                <CreateInstitutoForteOfUsePage />
              </AppGuard>
            ),
          },
        ],
      },

      // PRIVACY POLICY
      {
        path: 'privacy',
        children: [
          {
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="view">
                <PrivacyPolicyPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="create">
                <CreatePrivacyPolicyPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="edit">
                <CreatePrivacyPolicyPage />
              </AppGuard>
            ),
          },
        ],
      },

      // FAQ
      {
        path: 'faq',
        children: [
          {
            element: (
              <AppGuard module="appFaqs" action="view">
                <FaqPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appFaqs" action="create">
                <CreateFaqPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appFaqs" action="edit">
                <EditFaqPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories',
            element: (
              <AppGuard module="appFaqCategories" action="view">
                <FaqCategoriesPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories/create',
            element: (
              <AppGuard module="appFaqCategories" action="create">
                <CreateFaqCategoryPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories/:id',
            element: (
              <AppGuard module="appFaqCategories" action="edit">
                <EditFaqCategoryPage />
              </AppGuard>
            ),
          },
        ],
      },

      // SHOPPING SETTINGS
      {
        path: 'shopping-settings',
        children: [
          {
            element: (
              <AppGuard module="appClientData" action="view">
                <ShoppingSettingsPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'edit',
            element: (
              <AppGuard module="appClientData" action="edit">
                <ShoppingSettingsEditPage />
              </AppGuard>
            ),
          },
        ],
      },

      // USER LEVELS
      {
        path: 'user-levels',
        element: (
          <AppGuard module="appClientData" action="view">
            <UserLevelsPage />
          </AppGuard>
        ),
      },

      // NPS SEARCH
       {
        path: 'nps',
        element: (
          <AppGuard module="appClientData" action="view">
            <NpsSearchPage />
          </AppGuard>
        ),
      },

      // ESTABLISHMENTS
      {
        path: 'establishments',
        children: [
          {
            element: (
              <AppGuard module={['appStoresEstablishments', 'appFoodEstablishments', 'appServiceEstablishments']} action="view">
                <EstablishmentsPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module={['appStoresEstablishments', 'appFoodEstablishments', 'appServiceEstablishments']} action="create">
                <CreateEstablishmentPage />
              </AppGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <AppGuard module={['appStoresEstablishments', 'appFoodEstablishments', 'appServiceEstablishments']} action="edit">
                <EditEstablishmentPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories',
            element: (
              <AppGuard module="appEstablishmentCategories" action="view">
                <EstablishmentCategoriesPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories/create',
            element: (
              <AppGuard module="appEstablishmentCategories" action="create">
                <CreateEstablishmentCategoryPage />
              </AppGuard>
            ),
          },
          {
            path: 'categories/:id',
            element: (
              <AppGuard module="appEstablishmentCategories" action="edit">
                <EditEstablishmentCategoryPage />
              </AppGuard>
            ),
          },
        ],
      },

      // KEEP UP (FIQUE POR DENTRO)
      {
        path: 'keep-up',
        children: [
          {
            element: (
              <AppGuard module="appKeepUps" action="view">
                <KeepUpPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appKeepUps" action="create">
                <KeepUpFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appKeepUps" action="edit">
                <KeepUpFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // HIGHLIGHTS (DESTAQUES)
      // TODO: UNCOMMENT
      {
        path: 'highlights',
        children: [
          {
            element: (
              <AppGuard module="appHighlights" action="view">
                <HighlightsPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appHighlights" action="create">
                <HighlightFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appHighlights" action="edit">
                <HighlightFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // STORIES
      {
        path: 'stories',
        children: [
          {
            element: (
              <AppGuard module="appStories" action="view">
                <StoriesPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appStories" action="create">
                <StoryFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appStories" action="edit">
                <StoryFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // BANNERS
      {
        path: 'banners',
        element: (
          <AppGuard module="appBanners" action="view">
            <BannersPage />
          </AppGuard>
        ),
      },

      // HOME
      {
        path: 'home',
        children: [
          {
            element: (
              <AppGuard module="appHome" action="view">
                <HomePage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appHome" action="create">
                <HomeFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appHome" action="edit">
                <HomeFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // NOTIFICATIONS
      {
        path: 'notifications',
        children: [
          {
            index: true,
            element: (
              <AppGuard module="appNotifications" action="view">
                <NotificationCampaignsPage />
              </AppGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appNotifications" action="create">
                <CampaignNotificationFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <AppGuard module="appNotifications" action="edit">
                <CampaignNotificationFormPage />
              </AppGuard>
            ),
          },
          {
            path: 'proximity',
            element: (
              <AppGuard module="appProximityNotification" action="view">
                <ProximityNotificationsPage />
              </AppGuard>
            ),
          },
          {
            path: 'proximity/create',
            element: (
              <AppGuard module="appProximityNotification" action="create">
                <ProximityNotificationFormPage />
              </AppGuard>
            ),
          },
          {
            path: 'proximity/:id/edit',
            element: (
              <AppGuard module="appProximityNotification" action="edit">
                <ProximityNotificationFormPage />
              </AppGuard>
            ),
          },
          {
            path: 'logs',
            element: (
              <AppGuard module="appNotifications" action="view">
                <NotificationLogsPage />
              </AppGuard>
            ),
          },
          {
            path: 'segments',
            element: (
              <AppGuard module="appNotifications" action="view">
                <SegmentsListPage />
              </AppGuard>
            ),
          },
          {
            path: 'segments/create',
            element: (
              <AppGuard module="appNotifications" action="create">
                <SegmentFormPage />
              </AppGuard>
            ),
          },
          {
            path: 'segments/:id',
            element: (
              <AppGuard module="appNotifications" action="edit">
                <SegmentFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // MISSIONS (MISSÕES)
      {
        path: 'missions',
        children: [
          {
            index: true,
            element: (
              <AppGuard module="appMissions" action="view">
                <MissionsPage />
              </AppGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <AppGuard module="appMissions" action="create">
                <MissionFormPage />
              </AppGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <AppGuard module="appMissions" action="edit">
                <MissionFormPage />
              </AppGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'roulette',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: 'spins',
        element: (
          <AppGuard module="rouletteSpins">
            <RouletteSpinsPage />
          </AppGuard>
        ),
      },
      {
        path: 'prizes',
        element: (
          <AppGuard module="roulettePrizes" action="view">
            <RoulettePrizesPage />
          </AppGuard>
        ),
      },
      {
        path: 'prizes/create',
        element: (
          <AppGuard module="roulettePrizes" action="create">
            <RoulettePrizeFormPage />
          </AppGuard>
        ),
      },
      {
        path: 'prizes/:id',
        element: (
          <AppGuard module="roulettePrizes" action="edit">
            <RoulettePrizeFormPage />
          </AppGuard>
        ),
      },
    ],
  },
  {
    path: 'admin/roleta',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: 'spins',
        element: (
          <AppGuard module="rouletteSpins">
            <RouletteSpinsPage />
          </AppGuard>
        ),
      },
      {
        path: 'premios',
        element: (
          <AppGuard module="roulettePrizes" action="view">
            <RoulettePrizesPage />
          </AppGuard>
        ),
      },
      {
        path: 'premios/create',
        element: (
          <AppGuard module="roulettePrizes" action="create">
            <RoulettePrizeFormPage />
          </AppGuard>
        ),
      },
      {
        path: 'premios/:id',
        element: (
          <AppGuard module="roulettePrizes" action="edit">
            <RoulettePrizeFormPage />
          </AppGuard>
        ),
      },
    ],
  },
];
